import { MutableRefObject, ReactNode, useCallback, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useSidebar } from '../../hooks';
import { NavSidebarItem, SidebarNavigation, SidebarSettingsItem, SidebarSupportItem } from './SidebarNavigation';

type SidebarBaseChildrenArgs = {
  onPinClick: VoidFunction;
  pinButtonRef: MutableRefObject<HTMLButtonElement>;
};

type SidebarBaseProps = {
  children: (args: SidebarBaseChildrenArgs) => ReactNode;
  className?: string;
};

export function SidebarBase({ children, className }: SidebarBaseProps) {
  const [isPinned, setIsPinned] = useSidebar();
  const [isExpanded, setIsExpanded] = useState(isPinned);

  const pinButtonRef = useRef<HTMLButtonElement | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onMouseOver = useCallback(
    (e: React.MouseEvent) => {
      if (pinButtonRef.current && pinButtonRef.current.contains(e.target as Node)) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        return;
      }
      if (isExpanded) return;
      if (hoverTimeoutRef.current) return;

      hoverTimeoutRef.current = setTimeout(() => {
        setIsExpanded(true);
        hoverTimeoutRef.current = null;
      }, 300);
    },
    [isExpanded]
  );

  const onMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (!isPinned) {
      setIsExpanded(false);
    }
  }, [isPinned, setIsExpanded]);

  const onPinClick = useCallback(() => {
    setIsPinned((v) => {
      const newValue = !v;
      setIsExpanded(newValue);

      return newValue;
    });
  }, [setIsPinned]);

  return (
    <aside
      data-pinned={isPinned}
      data-expanded={isExpanded}
      onMouseMoveCapture={onMouseOver}
      onMouseLeave={onMouseLeave}
      onFocus={() => {}}
      className={twMerge(
        'group/sidebar peer absolute z-[98] flex h-sidebar-short w-sidebar-short flex-col gap-4 border border-white-100 border-r-secondary-border bg-white-100 px-3 py-5',
        'motion-safe:transition-all motion-safe:duration-500 motion-reduce:transition-none',
        'data-[expanded=false]:my-collapsed-sidebar data-[expanded=false]:ms-collapsed-sidebar data-[expanded=false]:rounded-xl data-[expanded=false]:border data-[expanded=false]:border-secondary-border',
        'data-[expanded=true]:h-main data-[expanded=true]:w-sidebar-full data-[expanded=true]:px-4 data-[expanded=true]:py-6',
        'data-[pinned=true]:w-sidebar-full',
        className
      )}
    >
      {children({ onPinClick, pinButtonRef })}
    </aside>
  );
}

SidebarBase.Navigation = SidebarNavigation;
SidebarBase.NavItem = NavSidebarItem;
SidebarBase.SettingsItem = SidebarSettingsItem;
SidebarBase.SupportItem = SidebarSupportItem;
